import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNoIndex } from "@/hooks/useNoIndex";
import StepIndicator from "@/components/internal/StepIndicator";
import { emptyOnboardingData, type OnboardingData } from "@/components/internal/types";
import { canProceedFromStep } from "@/components/internal/validation";
import ClientAccountStep from "@/components/internal/steps/ClientAccountStep";
import BusinessDetailsStep from "@/components/internal/steps/BusinessDetailsStep";
import PlanAddOnsStep from "@/components/internal/steps/PlanAddOnsStep";
import ConnectStep from "@/components/internal/steps/ConnectStep";
import ReviewStep from "@/components/internal/steps/ReviewStep";
import FinishStep from "@/components/internal/steps/FinishStep";

const STORAGE_KEY = "dibs_internal_onboarding";
const STEP_STORAGE_KEY = "dibs_internal_onboarding_step";
const STEP_LABELS = [
  "Account",
  "Business",
  "Plan",
  "Connect",
  "Review",
  "Finish",
];

const loadData = (): OnboardingData => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...emptyOnboardingData, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { ...emptyOnboardingData };
};

// Restore the step the operator was last on so a reload resumes in place
// instead of dropping back to Account. Clamped to a valid index.
const loadStep = (): number => {
  try {
    const raw = localStorage.getItem(STEP_STORAGE_KEY);
    if (raw == null) return 0;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return 0;
    return Math.min(Math.max(0, n), STEP_LABELS.length - 1);
  } catch {
    return 0;
  }
};

const OnboardingWizard = () => {
  useNoIndex();
  const navigate = useNavigate();
  const [step, setStep] = useState(loadStep);
  const [data, setData] = useState<OnboardingData>(loadData);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  // Persist the current step so a refresh resumes where the operator left off.
  useEffect(() => {
    localStorage.setItem(STEP_STORAGE_KEY, String(step));
  }, [step]);

  const update = (patch: Partial<OnboardingData>) =>
    setData((d) => ({ ...d, ...patch }));

  const reset = () => {
    setData({ ...emptyOnboardingData });
    setStep(0);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STEP_STORAGE_KEY);
  };

  const canProceed = canProceedFromStep(step, data);

  const isLast = step === STEP_LABELS.length - 1;
  const steps = [
    ClientAccountStep,
    BusinessDetailsStep,
    PlanAddOnsStep,
    ConnectStep,
    ReviewStep,
    FinishStep,
  ];
  const Current = steps[step];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b bg-card/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Home className="h-4 w-4" />
            Internal
          </button>
          <span className="font-heading font-semibold gradient-text">
            Onboarding Wizard
          </span>
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-4 w-4" />
            Start over
          </Button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-8 space-y-8">
        <StepIndicator steps={STEP_LABELS} current={step} />

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.22 }}
          >
            <Current data={data} update={update} />
          </motion.div>
        </AnimatePresence>

        {!isLast && (
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed}
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        {isLast && (
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button variant="ghost" onClick={reset}>
              <RotateCcw className="h-4 w-4" />
              New client
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default OnboardingWizard;
